import { keysToCamelCase } from "@jti/fe-common";
import type { MagentoOrderPrices } from "@jti/magento";
import type { MagentoApiCurrencyEnum, MagentoApiGetCustomerCartOrderPricesQueryResponse } from "@jti/magento-api-v2";

type CartMoney = MagentoOrderPrices["grandTotal"];

type RawMoney = { value?: number | null; currency?: MagentoApiCurrencyEnum | null } | null | undefined;

const toMoney = (m: RawMoney): CartMoney | undefined =>
  m?.value != null && m.currency != null
    ? { value: m.value, currency: m.currency as unknown as CartMoney["currency"] }
    : undefined;

export const normalizeGetCustomerCartPricesResponse = (
  response: MagentoApiGetCustomerCartOrderPricesQueryResponse
): MagentoOrderPrices | undefined => {
  if (!response?.cart) {
    return undefined;
  }

  const cart = keysToCamelCase(response.cart);

  const grandTotal = toMoney(cart.prices?.grandTotal);
  const subtotalIncludingTax = toMoney(cart.prices?.subtotalIncludingTax);
  const subtotal = toMoney(cart.prices?.subtotalExcludingTax);

  if (!grandTotal || !subtotalIncludingTax || !subtotal) {
    return undefined;
  }

  return {
    grandTotal,
    subtotalIncludingTax,
    subtotal,
    appliedTaxes: (cart.prices?.appliedTaxes ?? []).flatMap((t) => {
      const amount = toMoney(t?.amount);
      return t && amount ? [{ amount, label: t.label, rate: t.rate ?? undefined }] : [];
    }),
    discounts: (cart.prices?.discounts ?? []).flatMap((d) => {
      const amount = toMoney(d?.amount);
      return d && amount ? [{ amount, label: d.label, couponCode: d.couponCode ?? undefined }] : [];
    }),
    appliedCampaign: {
      loyaltyDiscountAmount: cart.appliedCampaign?.loyaltyDiscountAmount ?? 0,
      loyaltyDiscountUuid: cart.appliedCampaign?.loyaltyDiscountUuid ?? "",
    },
    shipping: toMoney(cart.shippingAddresses?.[0]?.selectedShippingMethod?.amount),
    estimatedShippingCost: toMoney(cart.estimatedShippingCost?.amount),
    minimumPayableAmount: toMoney(cart.minimumPayableAmount),
    subscriptionAmount:
      cart.subscriptionAmount != null ? { value: cart.subscriptionAmount, currency: grandTotal.currency } : undefined,
  };
};
